/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {Observable, of} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface EarningsAccountModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class EarningsAccountApiService extends SignalAdminResourceApiBase<EarningsAccountModel> {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'earningsaccounts', {}, () => localUserProfile?.isReadOnly() || false);
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<EarningsAccountModel>> {
    return this.client.request<SignalAdminResourcePage<EarningsAccountModel>>({
      method: 'GET',
      url: this.resourceUri('earningsaccounts'),
      params
    });
  }

  getMany(ids: string[]): Observable<SignalAdminResourcePage<EarningsAccountModel>> {
    if (!ids.length) {
      return of({results: [], totalCount: 0, page: 0});
    }
    return this.listOptions({
      ids,
      pageSize: ids.length,
      includeActive: true,
      includeInactive: true,
      includeHidden: true
    });
  }

  getApprovalNotifications(id: string): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri(`earningsaccounts/${encodeURIComponent(id)}/approvalnotifications`)
    });
  }

  acknowledgeApprovalNotifications(id: string, body: any): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(`earningsaccounts/${encodeURIComponent(id)}/approvalnotifications/acknowledge`),
      body
    });
  }

  recordApproval(id: string, body: any): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(`earningsaccounts/${encodeURIComponent(id)}/approve`),
      body
    });
  }

  getApprovalHistory(id: string, params: Record<string, any>, body: any): Observable<any> {
    return this.client.request({
      method: 'POST',
      url: this.resourceUri(`earningsaccounts/${encodeURIComponent(id)}/approvalhistory`),
      params,
      body
    });
  }
}
